import mirrorkey from 'mirrorkey';
import get from 'lodash/get';
import api from 'api';
import { init as initRecaptcha } from 'config/application/recaptcha';
import buildReducer from '../buildReducer';

export const initialState = {
  isCreatingLead: false,
  isCheckingCode: false,
  messageId: null,
  phoneNumber: null,
  timeToResend: 0
};

/**
 * @type {Object} ACTIONS
 * @property {string} FORM_LEAD_REQUEST
 * @property {string} FORM_LEAD_SUCCESS
 * @property {string} FORM_LEAD_FAILURE
 * @property {string} FORM_CODE_REQUEST
 * @property {string} FORM_CODE_SUCCESS
 * @property {string} FORM_CODE_FAILURE
 * @property {string} FORM_SEND_CODE_REQUEST
 * @property {string} FORM_SEND_CODE_SUCCESS
 * @property {string} FORM_SEND_CODE_FAILURE
 * @property {string} SET_TIME_TO_RESEND
 */
const ACTIONS = mirrorkey([
  'FORM_LEAD_REQUEST',
  'FORM_LEAD_SUCCESS',
  'FORM_LEAD_FAILURE',
  'FORM_CODE_REQUEST',
  'FORM_CODE_SUCCESS',
  'FORM_CODE_FAILURE',
  'FORM_SEND_CODE_REQUEST',
  'FORM_SEND_CODE_SUCCESS',
  'FORM_SEND_CODE_FAILURE',
  'SET_TIME_TO_RESEND'
]);

// ------------------------------------
// Actions
// ------------------------------------
export const postLead = (payload = {}) => dispatch => {
  dispatch({
    type: ACTIONS.FORM_LEAD_REQUEST,
    payload
  });

  const phoneNumber = get(payload, 'phoneNumber', '')
    .replace(/\+7 (.*)/g, '8$2')
    .replace(' ', '');

  return initRecaptcha()
    .then(recaptchaPublicKey =>
      window.grecaptcha.execute(recaptchaPublicKey, { action: 'create_lead' })
    )
    .then(token => api.landing.createLead({ phoneNumber, token }))
    .then(response => {
      dispatch({
        type: ACTIONS.FORM_LEAD_SUCCESS,
        payload: response.data
      });
      return response;
    })
    .catch(e => {
      dispatch({
        type: ACTIONS.FORM_LEAD_FAILURE
      });

      throw e;
    });
};

const setResetCodeInterval = (() => {
  let intervalId = null;
  return (dispatch, getState) => {
    window.clearInterval(intervalId);

    dispatch({
      type: ACTIONS.SET_TIME_TO_RESEND,
      payload: {
        timeToResend: 90 * 1000
      }
    });

    intervalId = window.setInterval(() => {
      const timeToResend = getState().form.timeToResend;

      if (timeToResend === 1000) {
        window.clearInterval(intervalId);
      }

      dispatch({
        type: ACTIONS.SET_TIME_TO_RESEND,
        payload: {
          timeToResend: timeToResend - 1000
        }
      });
    }, 1000);
  };
})();

export const requestCode = payload => (dispatch, getState) => {
  dispatch({
    type: ACTIONS.FORM_CODE_REQUEST,
    payload
  });

  const phoneNumber = getState().form.phoneNumber;

  return initRecaptcha()
    .then(recaptchaPublicKey =>
      window.grecaptcha.execute(recaptchaPublicKey, {
        action: 'authentication'
      })
    )
    .then(token => api.landing.requestCode({ phoneNumber, token }))
    .then(response => {
      dispatch({
        type: ACTIONS.FORM_CODE_SUCCESS,
        payload: response.data
      });

      setResetCodeInterval(dispatch, getState);

      return response.data;
    })
    .catch(e => {
      dispatch({
        type: ACTIONS.FORM_CODE_FAILURE
      });

      throw e;
    });
};

export const sendCode = payload => (dispatch, getState) => {
  dispatch({
    type: ACTIONS.FORM_SEND_CODE_REQUEST,
    payload
  });

  const { code } = payload;
  const { messageId, phoneNumber } = getState().form;

  return api.landing
    .sendCode({
      messageId,
      phoneNumber,
      code
    })
    .then(response => {
      dispatch({
        type: ACTIONS.FORM_SEND_CODE_SUCCESS,
        payload: response.data
      });
      return response.data;
    })
    .catch(e => {
      dispatch({
        type: ACTIONS.FORM_SEND_CODE_FAILURE
      });

      throw e;
    });
};

export const actions = {
  postLead,
  requestCode,
  sendCode
};

// ------------------------------------
// Reducers
// ------------------------------------
const leadRequest = state => ({
  ...state,
  isCreatingLead: true
});

const leadSuccess = (state, { integrationId, phoneNumber }) => ({
  ...state,
  isCreatingLead: false,
  accountId: integrationId,
  phoneNumber
});

const leadFailure = state => ({
  ...state,
  isCreatingLead: false
});

const codeSuccess = (state, { id }) => ({
  ...state,
  messageId: id
});

const sendCodeSuccess = state => ({
  ...state,
  isCheckingCode: false
});

const sendCodeRequest = state => ({
  ...state,
  isCheckingCode: true
});

const sendCodeFailure = state => {
  return {
    ...state,
    isCheckingCode: false
  };
};

const setTimeToResend = (state, { timeToResend }) => ({
  ...state,
  timeToResend
});

const ACTION_HANDLERS = {
  [ACTIONS.FORM_LEAD_REQUEST]: leadRequest,
  [ACTIONS.FORM_LEAD_SUCCESS]: leadSuccess,
  [ACTIONS.FORM_LEAD_FAILURE]: leadFailure,

  [ACTIONS.FORM_CODE_SUCCESS]: codeSuccess,
  [ACTIONS.FORM_SEND_CODE_SUCCESS]: sendCodeSuccess,
  [ACTIONS.FORM_SEND_CODE_REQUEST]: sendCodeRequest,
  [ACTIONS.FORM_SEND_CODE_FAILURE]: sendCodeFailure,
  [ACTIONS.SET_TIME_TO_RESEND]: setTimeToResend
};

export default buildReducer(ACTION_HANDLERS, initialState);
